// Dashboard symptom quick-log modal. Lets the user log a severity update for a
// recently-experienced symptom without going through chat (no Bedrock).
//
// Flow: pick → log (SymptomFollowUpCard) → done.
//  - pick:  top-25 symptoms by occurrence in the last 90 days, ranked by frequency.
//  - log:   the standard SymptomFollowUpCard UI (severity bar, Gone, no snooze).
//  - done:  success + "Log another" / "Done".
//
// Delegates the actual write to POST /symptom-follow-up via SymptomFollowUpCard,
// same path as the proactive follow-up cards in chat and on the dashboard.

import { useEffect, useMemo, useState } from "react";
import type { MedicalEvent, OpenSymptom, PainBodyMap, SymptomHistoryPoint } from "@medassist/shared";
import { symptomIdentityOf } from "@medassist/shared";
import { listEvents, submitFeedback, ApiError } from "../lib/api.js";
import { SymptomFollowUpCard } from "./SymptomFollowUpCard.js";
import { FeedbackThumbs } from "./FeedbackThumbs.js";
import { ActivityIcon } from "./icons.js";
import { ModalShell } from "./ModalShell.js";
import { ClearableInput, ClearableTextarea } from "./ClearableInput.js";

interface SymptomChoice {
  key: string;
  name: string;
  eventType: "symptom" | "headache";
  region?: string;
  side?: string;
  /** D-PAIN-4: present when this group has a pinpoint bodyMap. MUST be passed
   *  back to SymptomFollowUpCard, or the check-in's identity key drops the
   *  `|siteId` suffix and forks off a new, disconnected group. */
  siteId?: string;
  /** Optional user-supplied site label from the bodyMap, used to disambiguate
   *  two differently-located sites that share a name (e.g. two "knee" pains). */
  label?: string;
  count: number;
  currentSeverity: number;
  history: SymptomHistoryPoint[];
  lastUpdatedAt: string;
  firstReportedAt: string;
}

type Step = "pick" | "log" | "done";

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
/** How many choices to show when the search box is empty. When the user types a
 *  query we filter across the *full* set — otherwise a symptom logged only once
 *  (ranked last by frequency) would be unreachable even by an exact-name search. */
const MAX_CHOICES = 25;

function buildChoices(
  symptomEvents: MedicalEvent[],
  headacheEvents: MedicalEvent[],
): SymptomChoice[] {
  const cutoff = Date.now() - NINETY_DAYS_MS;

  interface Group {
    key: string;
    name: string;
    eventType: "symptom" | "headache";
    region?: string;
    side?: string;
    siteId?: string;
    label?: string;
    count: number;
    events: MedicalEvent[];
  }

  const groups = new Map<string, Group>();

  const allEvents = [...symptomEvents, ...headacheEvents]
    .filter((e) => !e.deleted && Date.parse(e.occurredAt) >= cutoff)
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt));

  for (const e of allEvents) {
    // D-PAIN-4: derive the site-aware identity through the same single source
    // buildOpenSymptoms/groupPainSites use, so a pinpointed pain site is never
    // merged with a differently-located one that happens to share a name.
    const id = symptomIdentityOf(e);
    if (!id) continue;
    const bm = (e.payload as { bodyMap?: PainBodyMap }).bodyMap;

    const g = groups.get(id.key);
    if (g) {
      g.count++;
      if (id.side) g.side = id.side;
      if (id.region) g.region = id.region;
      if (id.siteId) g.siteId = id.siteId;
      if (bm?.label) g.label = bm.label;
      g.eventType = e.eventType as "symptom" | "headache";
      g.events.push(e);
    } else {
      groups.set(id.key, {
        key: id.key, name: id.name, eventType: e.eventType as "symptom" | "headache",
        region: id.region, side: id.side, siteId: id.siteId, label: bm?.label,
        count: 1, events: [e],
      });
    }
  }

  return [...groups.values()]
    .sort((a, b) => b.count - a.count)
    .map((g) => {
      const latest = g.events[g.events.length - 1]!;
      const lp = latest.payload as Record<string, unknown>;
      const latestSeverity =
        typeof lp.severity === "number" && lp.severity > 0 ? lp.severity : 5;

      const history: SymptomHistoryPoint[] = g.events
        .slice(-10)
        .reverse()
        .map((ev) => ({
          at: ev.occurredAt,
          severity:
            typeof (ev.payload as Record<string, unknown>).severity === "number"
              ? ((ev.payload as Record<string, unknown>).severity as number)
              : 0,
        }));

      return {
        key: g.key,
        name: g.name,
        eventType: g.eventType,
        region: g.region,
        side: g.side,
        siteId: g.siteId,
        label: g.label,
        count: g.count,
        currentSeverity: latestSeverity,
        history,
        lastUpdatedAt: latest.occurredAt,
        firstReportedAt: g.events[0]!.occurredAt,
      };
    });
}

function choiceToOpenSymptom(c: SymptomChoice): OpenSymptom {
  return {
    key: c.key,
    name: c.name,
    eventType: c.eventType,
    region: c.region,
    side: c.side,
    siteId: c.siteId,
    currentSeverity: c.currentSeverity,
    lastUpdatedAt: c.lastUpdatedAt,
    firstReportedAt: c.firstReportedAt,
    history: c.history,
    dueForCheckIn: false,
    reminderIntervalHours: 6,
    nextDueAt: c.lastUpdatedAt,
  };
}

export function QuickSymptomLog({
  patientId,
  onClose,
  onLogged,
  onGoToChat,
}: {
  patientId: string;
  onClose: () => void;
  onLogged: (label: string) => void;
  onGoToChat: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<SymptomChoice[]>([]);
  const [query, setQuery] = useState("");

  const [step, setStep] = useState<Step>("pick");
  const [selected, setSelected] = useState<SymptomChoice | null>(null);
  const [loggedName, setLoggedName] = useState("");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [symptomEvents, headacheEvents] = await Promise.all([
          listEvents(patientId, "symptom"),
          listEvents(patientId, "headache"),
        ]);
        if (!cancelled) setChoices(buildChoices(symptomEvents, headacheEvents));
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : "Couldn't load recent symptoms");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return choices.slice(0, MAX_CHOICES);
    return choices.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.label ?? "").toLowerCase().includes(q) ||
        (c.region ?? "").toLowerCase().replace(/-/g, " ").includes(q),
    );
  }, [choices, query]);

  function pick(c: SymptomChoice) {
    setSelected(c);
    setError(null);
    setStep("log");
  }

  function handleDone(key: string) {
    const name = selected?.label ?? selected?.name ?? key;
    const displayName = name.charAt(0).toUpperCase() + name.slice(1);
    setLoggedName(displayName);
    onLogged(`Logged ${displayName}`);
    setStep("done");
  }

  async function handleListFeedback(
    rating: "up" | "down",
    opts: { reasonText?: string },
  ) {
    await submitFeedback({
      patientId,
      sessionId: "quick-symptom-log",
      mode: "capture",
      rating,
      sourceSurface: "quick_symptom_log",
      ...(rating === "down"
        ? { reasonCategory: "inaccurate" as const, reasonText: opts.reasonText }
        : {}),
      pendingProposal: {
        kind: "quick_symptom_log_list",
        patientId,
        query: query.trim() || undefined,
        shown: filtered.map((c) => ({ key: c.key, name: c.name, count: c.count })),
      },
    });
  }

  function logAnother() {
    setSelected(null);
    setError(null);
    setNote("");
    setShowNote(false);
    setQuery("");
    setStep("pick");
  }

  const title =
    step === "pick"
      ? "Log a symptom"
      : step === "log"
      ? (selected
          ? (selected.label ?? selected.name).charAt(0).toUpperCase() + (selected.label ?? selected.name).slice(1)
          : "Log a symptom")
      : "Symptom logged";

  return (
    <ModalShell
      title={title}
      icon={<ActivityIcon size={18} className="text-(--accent)" />}
      onClose={onClose}
      subheader={
        step === "pick" && !loading && choices.length > 0 ? (
          <ClearableInput
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search symptoms…"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
        ) : undefined
      }
      bodyClassName={"overflow-y-auto p-4" + (step === "pick" ? " h-[50vh]" : "")}
    >
      {error && (
            <p className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
          )}

          {/* ── Step: pick ─────────────────────────────────────────────── */}
          {step === "pick" && (
            <>
              {loading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading recent symptoms…</p>
              ) : choices.length === 0 ? (
                <div className="space-y-3 py-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    No symptoms logged in the last 90 days.
                  </p>
                  <button
                    onClick={() => { onClose(); onGoToChat(); }}
                    className="rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
                  >
                    Log via chat
                  </button>
                </div>
              ) : (
                <>
                  <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                    {query.trim()
                      ? "Every symptom logged in the last 90 days — tap one to log an update."
                      : `Your ${MAX_CHOICES} most-reported symptoms in the last 90 days — tap one to log an update, or search to find any other.`}
                  </p>
                  {filtered.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No matches.</p>
                  )}
                  <ul className="space-y-1">
                    {filtered.map((c) => {
                      // D-PAIN-4: prefer the site's own label to disambiguate two
                      // differently-located pains that share a name (e.g. two "knee" sites).
                      const displayName = c.label
                        ? c.label.charAt(0).toUpperCase() + c.label.slice(1)
                        : c.name.charAt(0).toUpperCase() + c.name.slice(1);
                      return (
                        <li key={c.key}>
                          <button
                            onClick={() => pick(c)}
                            className="flex w-full items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <span className="flex-1 font-medium text-gray-900 dark:text-gray-100">
                              {displayName}
                            </span>
                            {c.side && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {c.side}
                              </span>
                            )}
                            {c.region && c.region !== "head" && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {c.region.replace(/-/g, " ")}
                              </span>
                            )}
                            <span className="rounded-full bg-rose-100 dark:bg-rose-900/50 px-2 py-0.5 text-xs font-medium text-rose-700 dark:text-rose-300">
                              {c.count}×
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-3">
                    <button
                      onClick={() => { onClose(); onGoToChat(); }}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-700 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Log a different symptom via chat
                    </button>
                  </div>
                </>
              )}

              {!loading && (
                <div className="mt-4">
                  <FeedbackThumbs
                    prompt="Anything wrong with this list?"
                    placeholder="e.g. a symptom you logged isn't showing, or search isn't matching it (optional)"
                    onSubmit={handleListFeedback}
                  />
                </div>
              )}
            </>
          )}

          {/* ── Step: log ──────────────────────────────────────────────── */}
          {step === "log" && selected && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Reported {selected.count}× in the last 90 days. Adjust the severity and save.
              </p>
              {showNote ? (
                <ClearableTextarea
                  autoFocus
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note (optional)…"
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                />
              ) : (
                <button
                  onClick={() => setShowNote(true)}
                  className="text-xs text-gray-500 dark:text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  + Add a note
                </button>
              )}
              <SymptomFollowUpCard
                patientId={patientId}
                symptom={choiceToOpenSymptom(selected)}
                density="full"
                hideSnooze
                hideReminder
                onDone={handleDone}
                note={note}
                siteId={selected.siteId}
              />
              <button
                onClick={() => setStep("pick")}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Back
              </button>
            </div>
          )}

          {/* ── Step: done ─────────────────────────────────────────────── */}
          {step === "done" && (
            <div className="space-y-4 py-4 text-center">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                ✓ Logged {loggedName}.
              </p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={logAnother}
                  className="rounded-lg bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover)"
                >
                  Log another
                </button>
                <button
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  Done
                </button>
              </div>
            </div>
          )}
    </ModalShell>
  );
}
